/* eslint-disable no-loop-func */
  import gsap from "gsap";
import {
  Object3D,
  Group,
  CanvasTexture,
  MeshPhysicalMaterial,
  RepeatWrapping,
  Mesh,
  MeshStandardMaterial,
  MeshBasicMaterial,
  PointLight,
  RectAreaLight,
  MeshNormalMaterial,
  SpotLight,
  SpotLightHelper,
  BoxGeometry,
  Vector3,
  DoubleSide,
  Color,
  Vector2,
  Vector4
} from "three";

import { RectAreaLightHelper } from 'three/examples/jsm/helpers/RectAreaLightHelper.js';
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js';

import { FlakesTexture } from "three/examples/jsm/textures/FlakesTexture.js";
import { ScenesManager } from "../ScenesManager";
import { CameraManager } from "../CameraManager";
import TravelingParticles from "./TravelingParticles";

import { isMobile } from "utils/platform";
import { delay } from "utils/time";
import { APP_EVENTS, EventsManager } from "webgl/events/EventsManager";
import { applyMaterials, setFadePoints } from "./Car.parse";
import { availableFeatures } from "data/content.types";
import { groupBy } from "lodash";
import { SpriteType } from "./SpriteManager";
import { parseUrlQuery } from "utils/url";
import { Pane } from "tweakpane";
import { UIManager } from "../UIManager";

export class Car extends Object3D {
  transitioning = false;
  introDone = false;
  sizes: any;
  simTween!: gsap.core.Tween;
  mainLightTween!: gsap.core.Tween;
  model!: Group;
  seats!: Group;
  travelingParticles!: TravelingParticles;
  mainLight!: PointLight;
  globalEnvMapIntensity = 0;
  glassesEnvMapIntensity = 0;

  carPaintMaterial!: MeshStandardMaterial;
  carBlackoutTransitionSetttings = {
    from: {
      uSampler: 0.978,
      clearcoat: 0.0,
      clearcoatRoughness: 0.07,
      metalness: 0.5,
      roughness: 0.2,
    },
    to: {
      uSampler: 0.0,
      metalness: .6, 
      roughness: .2, 
      clearcoat: 1.0, 
      clearcoatRoughness: .015,
      color_r : 0.94,
      color_g : 0.09,
      color_b : 0.09,
    }
  }

  outterContainer!: Group;
  innerContainer!: Group;
  editPane: null | Pane = null;

  constructor() {
    super();

    RectAreaLightUniformsLib.init();

    this.build();
  }

  build() {
    this.sizes = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    this.setObject();

  }

  setObject() {
    this.outterContainer = new Group();
    // this.outterContainer.scale.set(1.015, 1.018, 1.018);
    this.add(this.outterContainer);
    this.innerContainer = new Group();
    this.add(this.innerContainer);
    
    const normalMap3 = new CanvasTexture(new FlakesTexture());
    normalMap3.wrapS = RepeatWrapping;
    normalMap3.wrapT = RepeatWrapping;
    normalMap3.repeat.x = 64;
    normalMap3.repeat.y = 64;
    normalMap3.anisotropy = 16;

    // car paint
    this.carPaintMaterial = new MeshPhysicalMaterial({
      clearcoat: this.carBlackoutTransitionSetttings.from.clearcoat,
      clearcoatRoughness: 0.07,
      metalness:this.carBlackoutTransitionSetttings.from.metalness,
      roughness: this.carBlackoutTransitionSetttings.from.roughness,
      color: 0x000000,
      normalMap: normalMap3,
      // normalScale: new Vector2(0.01, 0.01),
      envMap: ScenesManager.assets.hdrCar,
      envMapIntensity: 0.25,
    });


    this.setHits();
    this.setTravelingParticles();
    
    this.model = ScenesManager.assets.car.scene.clone();
    this.model.position.set(0, -0.85, 0);

    const radialEnvsModel = ScenesManager.assets.radialEnvs.scene.clone();
    const fadePoints: { point: Vector4, smoothness: Number, stretch: Vector2 }[] = [];

    radialEnvsModel.traverse((child) =>{

      const name = child.name.replace("sprite_", "");
      child.userData.featureName = name;


      const featureExternal = ScenesManager.EXTERNAL_FEATURES_DATA[name];
      
      if ((featureExternal && featureExternal.active)) {     
        fadePoints.push({
          point: new Vector4(child.position.x, child.position.y, child.position.z, child.userData.radius ? child.userData.radius : 1),
          smoothness: child.userData.smoothness ? child.userData.smoothness : 0.1,
          stretch: child.userData.stretch ? new Vector2(child.userData.stretch[0], child.userData.stretch[1]) : new Vector2(1, 1),
        });
      } else if (name.indexOf('rodas') > -1 && ScenesManager.EXTERNAL_FEATURES_DATA['rodas'] && ScenesManager.EXTERNAL_FEATURES_DATA['rodas'].active) {
        fadePoints.push({
          point: new Vector4(child.position.x, child.position.y, child.position.z, child.userData.radius ? child.userData.radius : 1),
          smoothness: child.userData.smoothness ? child.userData.smoothness : 0.1,
          stretch: child.userData.stretch ? new Vector2(child.userData.stretch[0], child.userData.stretch[1]) : new Vector2(1, 1),
        });
      } else if (name.indexOf('lanternaTraseira') > -1 && ScenesManager.EXTERNAL_FEATURES_DATA['lanternaTraseira'] && ScenesManager.EXTERNAL_FEATURES_DATA['lanternaTraseira'].active) {
        fadePoints.push({
          point: new Vector4(child.position.x, child.position.y, child.position.z, child.userData.radius ? child.userData.radius : 1),
          smoothness: child.userData.smoothness ? child.userData.smoothness : 0.1,
          stretch: child.userData.stretch ? new Vector2(child.userData.stretch[0], child.userData.stretch[1]) : new Vector2(1, 1),
        });
      }
      
    })

    if (fadePoints.length > 0) {
      setFadePoints(ScenesManager.TOTAL_FEATURES.externalActive === ScenesManager.TOTAL_FEATURES.external && ScenesManager.EXTERNAL_REVEALEAD, fadePoints);
    }
    
    applyMaterials(
       this.model,
      this.carPaintMaterial, 
      {
        progress: {value: this.carBlackoutTransitionSetttings.from.uSampler},
        uSampler: {value: ScenesManager.assets.carBlackoutTexture }
      },
      this.globalEnvMapIntensity,
      this.glassesEnvMapIntensity,
    )
    this?.add(this.model);

    this.seats = ScenesManager.assets.carSeats.scene.clone()
    this.seats.traverse(child => {
      if (child && (child as Mesh).material && child.name.indexOf('interior') > -1) {
        ((child as Mesh).material as MeshStandardMaterial) =  new MeshStandardMaterial({
          color: 0x616161,
          metalness: 1,
          roughness: 1,
          envMap: ScenesManager.assets.hdrCar,
          side: DoubleSide
        });   
      }
    })

    this.seats.position.set(0, -0.85, 0);
    this.add(this.seats)

    this.setLights();

    // if (parseUrlQuery().editMode === "true")
    // {
    //   this.setupEditMode()
    // }

  }

  setHits() {
    const histMaterial = new MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0,
      depthTest: false,
    });

    const hitsModel = ScenesManager.assets.featureHits.scene.clone();
    hitsModel.position.set(0, -0.8, 0);
    hitsModel.traverse((child) => {
      if (child) {
        child.renderOrder = 2;

        (child as Mesh).material = histMaterial;
        child.userData.featureName = child.name;
        child.userData.area = 'exterior';
        const featureData = ScenesManager.EXTERNAL_FEATURES_DATA[child.name];
        if ((featureData && featureData.active)) {
          if (ScenesManager.featureTraveling.indexOf(child.name) < 0) {
						ScenesManager.featureTraveling.push(child.name)
					}
          ScenesManager.featureHits.push(child);
        } else {
          child.visible = false;
        }
      }
    });

    

    const eyeSpritesModel = ScenesManager.assets.eyeSprites.scene.clone();

    eyeSpritesModel.traverse((child) =>{

      const name = child.name.replace("sprite_", "");
      child.userData.featureName = name;

      const featureExternal = ScenesManager.EXTERNAL_FEATURES_DATA[name];
      const featureInternal =  ScenesManager.INTERNAL_FEATURES_DATA[name];

      if ((featureExternal && featureExternal.active)) {
        if (name !== ScenesManager.lastAddedFeature?.name)
        {
          ScenesManager.sprites.add_sprite_at(name, "exterior" ,child.position, SpriteType.EYE)
        }
        else
        {          
          ScenesManager.sprites.add_sprite_at(name, "exterior" ,child.position,  ScenesManager.EXTERNAL_REVEALEAD ? SpriteType.EYE : SpriteType.UNLOCK)
        }
      }

      if ((featureInternal && featureInternal.active)) {
        if (name !== ScenesManager.lastAddedFeature?.name)
        {
          ScenesManager.sprites.add_sprite_at(name, "interior" ,child.position, SpriteType.EYE)
        }
        else
        {
          ScenesManager.sprites.add_sprite_at(name, "interior" ,child.position, ScenesManager.INTERNAL_REVEALEAD ? SpriteType.EYE : SpriteType.UNLOCK)
        }
      }
    })

    

    this.outterContainer.add(hitsModel);

    const hitsModelInterior = ScenesManager.assets.featureHitsInterior.scene.clone();
    hitsModelInterior.position.set(0, -0.8, 0);
    hitsModelInterior.traverse((child) => {
      if (child) {
        child.renderOrder = 2;

        (child as Mesh).material = histMaterial;
        child.userData.featureName = child.name;
        child.userData.area = 'interior';
        const featureInteriorData = ScenesManager.INTERNAL_FEATURES_DATA[child.name];
        if ((featureInteriorData && featureInteriorData.active)) {
          if (ScenesManager.featureTravelingInterior.indexOf(child.name) < 0) {
						ScenesManager.featureTravelingInterior.push(child.name)
					}
          ScenesManager.featureHits.push(child);
        } else {
          child.visible = false;
        }
      }
    });
    this.innerContainer.scale.set(0,0,0);
    this.innerContainer.add(hitsModelInterior);

    EventsManager.instance.trigger(APP_EVENTS.FEATURES_COUNT_COMPLETE, {
      total: ScenesManager.featureHits.length
    })

    document.addEventListener("click", () => {
      // if (ScenesManager.inFeature) return;
      // const area = ScenesManager.inInterior ? 'interior' : 'exterior';
      // const featureTargets = groupBy(ScenesManager.featureHits, o => o.userData.area === area)
      // let hits = ScenesManager.featureHits
      // if (featureTargets && featureTargets.true) {
      //   hits = featureTargets.true
      // }
      // const intersects = ScenesManager.raycaster.intersectObjects(
      //   hits
      // );
      // if (intersects.length > 0) {
      //   const rayTarget = intersects[0].object;
      //   if (rayTarget && rayTarget.userData.featureName) {
      //     const target =
      //       ScenesManager.EXTERNAL_FEATURES_DATA[rayTarget.userData.featureName];
      //     const targetInterior =
      //       ScenesManager.INTERNAL_FEATURES_DATA[rayTarget.userData.featureName];
      //       if ((target && !ScenesManager.inInterior) || (targetInterior && ScenesManager.inInterior)) {
      //         const coordinates = !ScenesManager.inInterior ? target.coordinates : targetInterior.coordinates;
      //         const targetCoords = isMobile()
      //         ? coordinates.mobile
      //         : coordinates.desktop;

      //         let boxTarget = rayTarget;
      //         if (isMobile())
      //         {
      //             let boxIndicator = ScenesManager.assets.mobileBoxes.scene.clone().getObjectByName(`mobile_box_${rayTarget.userData.featureName}`)
                  
      //             if (boxIndicator !== undefined) {
      //                 boxIndicator!.position.y -= 0.85
      //                 boxTarget = boxIndicator
      //             }
      //         }

      //         if (targetCoords) {
      //           ScenesManager.currentFeatureName = rayTarget.userData.featureName;
      //           CameraManager.goToFeature(targetCoords.pos, targetCoords.lookAt, boxTarget);
      //         }
      //     }
      //   }
      // }
    });
  }

  setTravelingParticles() {
    this.travelingParticles = new TravelingParticles(this.innerContainer);
    this.travelingParticles.position.set(0, -0.85, -0.033);
    this.outterContainer.add(this.travelingParticles)
    this.travelingParticles.updateOnce()
  }

  setLights() {
     const data = {
      topLight: {
        color: 0xff0000,
        intensity: 70,
        distance: 1.838,
        decay: 0.870,
      }
    };
    this.mainLight = new PointLight(data.topLight.color, data.topLight.intensity, data.topLight.distance, data.topLight.decay);
    this.mainLight.position.set(0, 1.304, 1.221);
    this.mainLightTween = gsap.fromTo(this.mainLight.position, 
      { z: 3.2

      },
      {
        z: -3.2,
        duration: 3,
        repeat: -1,
        ease: "power2.out"
      });
    this.add(this.mainLight);
   
  }

  show() {
    return new Promise(async (resolve) => {      
      this.transitioning = true;
      this.mainLightTween.kill(); 
      this.mainLightTween = gsap.to(this.mainLight.position, 
        {
          x: 0,
          y: 1.304,
          z: 1.221,
          duration: 2,
          ease: "power2.out",
          onComplete:()=> {
            this.mainLightTween.kill(); 
          }
        });

      
      await delay(0.6);
      let hasCarpaint = false;
      
      for (let key in ScenesManager.featureActiveObjs) {
        ScenesManager.featureActiveObjs[key].forEach((child: Mesh, index: number) => {
          if (child.userData.material) {
            gsap.to(child.userData.uniforms.progress, {
              value: 0,
              duration: 1,
              delay: 0.8 + (index / 10),
            })
          }  

          if (key === availableFeatures.assinaturaDeDesign) {
            hasCarpaint = true;
            child.userData.material.envMapIntensity = 0.05;
            gsap.to(child.userData.material, {
              clearcoat: this.carBlackoutTransitionSetttings.to.clearcoat,
              clearcoatRoughness: this.carBlackoutTransitionSetttings.to.clearcoatRoughness,
              metalness: this.carBlackoutTransitionSetttings.to.metalness,
              roughness: this.carBlackoutTransitionSetttings.to.roughness,
              duration: 2,
              // delay: 0.8,
            });
            //set car color
            gsap.to(child.userData.material.color, {
              r: 0.35,
              g: 0.03,
              b: 0.03,
              // r: 1,
              // g: 1,
              // b: 1,
              duration: 2,
            });
            gsap.to(this.mainLight, {
              intensity: 0,
              duration: 0.6,
              delay: .5,
            });
          } 
        });
      }

      if (!hasCarpaint) {
        gsap.to(this.mainLight, {
          intensity: 2,
          duration: 0.6,
          delay: .5,
        });
      //   gsap.to(this.mainLight, {
      //     intensity: 10,
      //     duration: 0.6,
      //     delay: 2.5,
      //     onComplete: () => {
      //       // this.remove(this.mainLight);
      //     }
      //   });
      }

      if (ScenesManager.debugMode) {
        CameraManager.playingIntro = false;
        CameraManager.isRotating = false;
        this.travelingParticles?.show()
        this.introDone = true;
        resolve(true);
        
        return
      }
      if (this.travelingParticles) {
        
        this.model.traverse((child) => {
          if (child && (child as Mesh).material) {
            const material = ((child as Mesh).material as MeshStandardMaterial);
            if (material) {

              if (ScenesManager.TOTAL_FEATURES.externalActive === ScenesManager.TOTAL_FEATURES.external && ScenesManager.EXTERNAL_REVEALEAD) {
                if (child.name.indexOf('body') > -1 || child.name.indexOf('kicksLogo') > -1 || child.name.indexOf('Placa') > -1 || child.name.indexOf('nameplate') > -1 || child.name.indexOf('easterEggs_badge') > -1 ) {
                  
                  gsap.to(child.userData.uniforms.progress, {
                    value: 0,
                    duration: 1,
                    delay: 1,
                  })
                }
              }

              if ((child.name.indexOf('kicksLogo') > -1 && ScenesManager.EXTERNAL_FEATURES_DATA['coluna'].active) || ((child.name.indexOf('easterEggs_badge') > -1 || child.name.indexOf('Placa') > -1 || child.name.indexOf('grade') > -1) && ScenesManager.EXTERNAL_FEATURES_DATA['farolGrade'].active)) {
                gsap.to(child.userData.uniforms.progress, {
                  value: 0,
                  duration: 1,
                  delay: 1,
                })
              }

              
    
            if (child && child.name.indexOf('farois_dianteiros') > -1 || child.name.indexOf('lanternas_dianteiras') > -1) {
              const color = ScenesManager.EXTERNAL_FEATURES_DATA['farolGrade'].active ? new Color(0xFFFFFF) : new Color(0x000000);
              (child as Mesh).material = new MeshStandardMaterial({
                color: color, 
                opacity: 1.0, 
                emissive: color, 
                emissiveIntensity: child.name.indexOf('lanternas_dianteiras') > -1 ? 5 : 20.0
              });
            }

              let intensity = (child.userData.isFeature && !child.userData.isFeatureActive && child.name.indexOf('headlights') < 0)
                                ? 0 
                                : child.name.indexOf('feature_assinaturaDeDesign') > -1 ? 3 :
                                (
                                  (( 
                                  child.name === 'Mesh002') &&
                                  child.userData.isFeatureActive) ||
                                  child.name.indexOf('glass') > -1) 
                                ? 1
                                : child.name.indexOf('headlights') > -1 ?  .5 
                                : child.name.indexOf('kicksLogo') > -1 ?  2 
                                : child.name === 'feature_rodas' ? 4
                                // : child.name.indexOf('body_feature_soleira') > -1 && child.userData.isFeatureActive? 2
                                : 1;
              // let intensity = 1;
              if (child.name.indexOf('easterEggs_badge') > -1) {
                intensity = 1;
              
              } else if (child.name.indexOf('Placa') > -1) {              
                intensity = 2;
              
              } else if (child.name.indexOf('body') > -1) {                
                intensity = (child.name.indexOf('body001') > -1) ? 2.5 : 4;
                if (child.name.indexOf('body_retrovisor') > -1) {                  
                  intensity = child.userData.isFeatureActive ? 6 : 0;
                }
              
              } else if (child.name.indexOf('feature_soleira') > -1) {                
                intensity = 2;
                if (!child.userData.isFeatureActive && !ScenesManager.EXTERNAL_FEATURES_DATA['farolGrade'].active) {
                  const material = new MeshBasicMaterial({color: 0x000000});
                  (child as Mesh).material = material;
                  intensity = 0;
                }
                                            
              } else if (child.name.indexOf('feature_retrovisor') > -1) {
                intensity = (child.userData.isFeatureActive) ? 6 : 0;
              
              } else if (child.name.indexOf('feature_coluna') > -1) {                
                intensity = (child.userData.isFeatureActive) ? 8 : 0;

              } 
              
              else if (ScenesManager.featureHits.length < ScenesManager.TOTAL_FEATURES.external && (child.name.indexOf('body') > -1 && !child.userData.isFeatureActive)) {
                intensity = 0;
                
              }
              gsap.to(material, {
                envMapIntensity: intensity,
                duration: 6,
                ease: "power0.inOut",
              });
              
            }
          }
        });

        await this.travelingParticles?.show()
       
        await delay(1); 
        this.introDone = true;
        CameraManager.playingIntro = false;
        CameraManager.isRotating = false;
        resolve(true);

      } else {
        this.introDone = true;
        resolve(true);
      }
    });
  }

  hide() {
    return new Promise(async (resolve) => {
      this.simTween?.kill();
      this.simTween = gsap.to(this.model.scale, {
        x: 0,
        y: 0,
        z: 0,
        duration: 1,
        onComplete: () => {
          this.introDone = false;
          this.transitioning = false;
          resolve(true);
        },
      });
    });
  }

  animate() {
    this.travelingParticles?.update(ScenesManager.clock.getElapsedTime());
  }

  setupEditMode() {
    if (!this.editPane)
    {
      let folder = UIManager.editPane.addFolder({title: "Car Settings"})

      let material_properties_list = [
        "roughness",
        "metalness",
        "envMapIntensity",
        "color"
      ]

      let feature_list = [
        "feature_assinaturaDeDesign",
        "feature_rodas",
        "feature_soleira",
        "feature_coluna",
        "feature_retrovisor",
        "feature_farolGrade",
        "feature_lanternaTraseira",
        "feature_tetoSolar"
      ]

      feature_list.forEach((feature) =>{
        let feature_folder = folder.addFolder({title: feature, expanded: false});
        let feature_mesh = this.model.getObjectByName(feature)

        if (feature_mesh) {
          let mesh = feature_mesh as Mesh
          let material = (mesh.material as MeshPhysicalMaterial)

          material_properties_list.forEach((property) =>{

            if (property === "color")
            {
                let color = new Color();
                if (feature == "feature_assinaturaDeDesign")
                  color.set(this.carBlackoutTransitionSetttings.to.color_r * 255, this.carBlackoutTransitionSetttings.to.color_g * 255, this.carBlackoutTransitionSetttings.to.color_b * 255)
                let params = {
                  color: color
                }

                feature_folder.addBinding(params, "color", {
                  label: property,
                }).on('change', (ev) => {
                  material.color.set(ev.value.r / 255.0, ev.value.g / 255.0, ev.value.b / 255.0)
                })
            }
            else
            {
              feature_folder.addBinding(material, property, {
                label: property,
                min: 0,
                max: 1,
                value: material[property]
              })
            }



          })
        }
      })



    }
  }




}
